import { ReactNode, useState, useRef, useEffect } from 'react'
import { FloatingPortal } from '@/components/FloatingPortal'
import { useFloatingPosition } from '@/hooks/useFloatingPosition'
import { getFocusModality } from '@/utils/focusModality'

export type TooltipPlacement = 'top' | 'bottom' | 'left' | 'right'

interface TooltipProps {
	label: string
	placement?: TooltipPlacement
	offset?: number
	className?: string
	/** Controlled visibility. When set, hover/focus triggers are disabled. */
	open?: boolean
	/** Use `status` for messages announced in response to a user action. */
	role?: 'tooltip' | 'status'
	children: ReactNode
}

export function Tooltip ({
	label,
	placement = 'top',
	offset = 8,
	className = '',
	open,
	role = 'tooltip',
	children
}: TooltipProps) {
	const controlled = open !== undefined
	const [hovered, setHovered] = useState(false)
	const visible = controlled ? open : hovered
	const timeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined)
	const anchorRef = useRef<HTMLSpanElement>(null)
	const floatingRef = useRef<HTMLSpanElement>(null)

	const { style } = useFloatingPosition(anchorRef, floatingRef, {
		open: visible,
		placement,
		align: 'center',
		offset
	})

	const show = () => {
		if (controlled) return
		// A click lands focus on top of a pending hover timer; overwriting the ref
		// orphans that timer, so `hide()` on blur can't cancel it and the tooltip
		// pops up after a modal opened by the click — promoted above it.
		clearTimeout(timeoutRef.current)
		timeoutRef.current = setTimeout(() => { setHovered(true) }, 300)
	}
	// Pointer focus is already covered by hover. Skipping it also keeps the
	// tooltip down when a closing modal returns focus to its trigger.
	const showOnFocus = () => {
		if (getFocusModality() === 'pointer') return
		show()
	}
	const hide = () => {
		if (controlled) return
		clearTimeout(timeoutRef.current)
		setHovered(false)
	}

	useEffect(() => () => clearTimeout(timeoutRef.current), [])

	return (
		<span
			ref={anchorRef}
			className={`spaces:relative spaces:inline-flex ${className}`}
			onMouseEnter={show}
			onMouseLeave={hide}
			onFocus={showOnFocus}
			onBlur={hide}
			// Pressing the trigger dismisses the tooltip. A modal it opens covers a
			// stationary pointer without firing mouseleave, and Safari doesn't focus
			// clicked buttons, so neither handler above would clear it — leaving the
			// tooltip showing once the modal closes.
			onPointerDown={hide}
		>
			{children}
			{visible && (
				<FloatingPortal>
					<span
						ref={floatingRef}
						role={role}
						style={style}
						className='spaces-tooltip spaces:fixed spaces:z-[2147483200] spaces:px-8 spaces:py-4 spaces:text-12/16 spaces:w-max spaces:max-w-[200px] spaces:font-default spaces:text-content-inverse spaces:bg-canvas-inverse spaces:rounded-4 spaces:shadow-sm-below spaces:pointer-events-none'
					>
						{label}
					</span>
				</FloatingPortal>
			)}
		</span>
	)
}
